/**
  * Message verbs (AzureMessage.kind) values we can get.
  */
class Verbs {
    // Send when ready to start initialization.
    public static postReady = "ready";
    // Send when ready for user interaction.
    public static initializationComplete = "initializationcomplete";

    // Below here are messages we define - these should match the ones in CreateBladeViewModel.ts

    public static getInfo = "get-info";
    public static receiveInfo = "info";

    public static getAuthTokens = "get-auth-tokens";
    public static receiveAuthTokens = "auth-tokens";

    public static getEndpointStatus = "get-endpoint-status";
    public static receiveEndpointStatus = "endpoint-status";

    public static getAppInsightsStatus = "get-appinsights-status";
    public static receiveAppInsightsStatus = "appinsights-status";
}

// #region: Message body definitions.
// These are defined by us and should match the one in CreateBladeViewModel.ts

export interface InfoResult {
    version: string;
}

/** Access tokens message body */
export interface AccessTokensResult {
    armToken: string;
    selfToken: string;
}

/** Endpoint/Bot status */
export interface EndpointStatusResult {
    status: "ready" | "not-ready" | "error" | "no-endpoint";
    description?: string;
}

/** AppInsights status */
export interface AppInsightsStatusResult {
    status: "ready" | "not-ready" | "error" | "not-configured";
    description?: string;
}

// #endregion: Message body definitions.

/**
  * Message coming from the Azure blade.
  */
interface AzureMessage {
    signature: string;
    kind?: string;
    data: {} | AccessTokensResult | EndpointStatusResult | undefined;
}

/**
  * Event/Message coming from the Azure blade.
  */
interface AzureEvent extends Event {
    origin: string;
    data: AzureMessage;
}

export const DefaultPortalSignature = "FxFrameBlade";

/**
  * General helper for messaging back and forth between the Azure blade and the site loaded in an IFrame.
  */
export class AzurePortalMessenger {
    private AzurePortalSignature;
    private _messageHandler: EventListener;
    private _trustedOrigin: string | null = null;
    private _receivedMessageHandlers = {};

    public constructor(origin: string, frameSignature: string) {
        // todo: Validate this is portal.azure.com so other domains can't send us messages.
        this._trustedOrigin = origin;
        // The AzureMessage.signature value we will get. The new blade uses a FrameControl - the older extension uses an FxFrameBlade.
        this.AzurePortalSignature = frameSignature || DefaultPortalSignature;

        // Start listening for messages if needed.
        if (!this._messageHandler) {
            this._messageHandler = this._onReceiveMessage.bind(this);
            window.addEventListener("message", this._messageHandler, false);
        }
    }

    /**
      * Let the Azure blade know we are ready to receive messages.
      */
    public postReady(): void {
        this._postMessageToAzurePortal(Verbs.postReady);
    }

    public initializationComplete(): void {
        this._postMessageToAzurePortal(Verbs.initializationComplete);
    }

    public requestInfo(): void {
        this._postMessageToExtension(Verbs.getInfo);
    }

    public listenForInfo(callback: (tokens: InfoResult) => void): void {
        this._listenForMessage(callback, Verbs.receiveInfo);
    }

    public unlistenForInfo(callback: (tokens: InfoResult) => void): void {
        this._unlistenForMessage(callback, Verbs.receiveInfo);
    }

    /**
     * Request auth tokens - also starts polling in the azure blade for updates to the tokens.
     */
    public requestAuthenticationTokens(): void {
        this._postMessageToExtension(Verbs.getAuthTokens);
    }

    /**
     * When Azure sends us auth tokens call the callback method with the message.
     */
    public listenForAuthenticationTokens(callback: (tokens: AccessTokensResult) => void): void {
        this._listenForMessage(callback, Verbs.receiveAuthTokens);
    }

    /**
      * Stop listening - opposite of listenForAuthenticationTokens.
      */
    public unlistenForAuthenticationTokens(callback: (tokens: AccessTokensResult) => void): void {
        this._unlistenForMessage(callback, Verbs.receiveAuthTokens);
    }

    /**
     * Request the bot endpoing status.
     */
    public requestEndpointStatus(): void {
        this._postMessageToExtension(Verbs.getEndpointStatus);
    }

    /**
     * When Azure sends us the bot endpoint status call the callback method.
     */
    public listenForEndpointStatus(callback: (endpointStatus: EndpointStatusResult) => void): void {
        this._listenForMessage(callback, Verbs.receiveEndpointStatus);
    }

    /**
     * Stop listening for endpoint status messages - opposite of listenForEndpointStatus.
     */
    public unlistenForEndpointStatus(callback: (endpointStatus: EndpointStatusResult) => void): void {
        this._unlistenForMessage(callback, Verbs.receiveEndpointStatus);
    }

    public requestAppInsightsStatus(): void {
        this._postMessageToExtension(Verbs.getAppInsightsStatus);
    }

    public listenForAppInsightsStatus(callback: (appInsightsStatus: AppInsightsStatusResult) => void): void {
        this._listenForMessage(callback, Verbs.receiveAppInsightsStatus);
    }

    public unlistenForAppInsightsStatus(callback: (appInsightsStatus: AppInsightsStatusResult) => void): void {
        this._unlistenForMessage(callback, Verbs.receiveAppInsightsStatus);
    }

    private _listenForMessage(callback: (message: any) => void, messageVerb: string): void {
        if (!callback) {
            throw new Error("callback must be defined.");
        }

        (this._receivedMessageHandlers[messageVerb] = this._receivedMessageHandlers[messageVerb] || []).push(callback);
    }

    private _unlistenForMessage(callback: (message: any) => void, messageVerb: string): void {
        if (this._receivedMessageHandlers[messageVerb] && callback) {
            const index = this._receivedMessageHandlers[messageVerb].indexOf(callback);
            if (index > -1) {
                this._receivedMessageHandlers[messageVerb].splice(index, 1);
            }
        }
    }

    private _postMessageToAzurePortal(messageKind: string): void {
        this._postMessageToAzure( {
            signature: this.AzurePortalSignature,
            kind: messageKind
        } as AzureMessage);
    }

    // Apparently, within Ibiza's iframe control, messages with 'kind' are directed to the portal,
    // otherwise they go to the extension.
    // This doesn't seem to apply to iframe blades.
    private _postMessageToExtension(data: {} | AccessTokensResult | EndpointStatusResult | undefined): void {
        if (this.AzurePortalSignature === DefaultPortalSignature) {
            this._postMessageToAzure({
                signature: this.AzurePortalSignature,
                kind: data,
                data: null
            } as AzureMessage);
        } else {
            this._postMessageToAzure({
                signature: this.AzurePortalSignature,
                data: data
            } as AzureMessage);
        }
    }

    private _postMessageToAzure(message: AzureMessage): void {
        if (!this._trustedOrigin) {
            throw new Error("Must set trusted origin before posting messages.");
        }

        window.parent.postMessage(message, this._trustedOrigin);
    }

    private _onReceiveMessage(event: Event): void {
        const azureEvent = event as AzureEvent;

        if (!azureEvent.origin || !azureEvent.data) {
            return;
        }

        // Verify the message came from Azure.
        if (azureEvent.origin !== this._trustedOrigin) {
            return;
        }

        switch (azureEvent.data.kind) {
            case Verbs.receiveAuthTokens:
                this._invokeHandlers(Verbs.receiveAuthTokens, azureEvent.data.data as AccessTokensResult);
                break;
            case Verbs.receiveInfo:
                this._invokeHandlers(Verbs.receiveInfo, azureEvent.data.data as InfoResult);
                break;
            case Verbs.receiveEndpointStatus:
                this._invokeHandlers(Verbs.receiveEndpointStatus, azureEvent.data.data as EndpointStatusResult);
                break;
            case Verbs.receiveAppInsightsStatus:
                this._invokeHandlers(Verbs.receiveAppInsightsStatus, azureEvent.data.data as AppInsightsStatusResult);
                break;
        }
    }

    private _invokeHandlers<T>(verb: string, message: T): void {
        (this._receivedMessageHandlers[verb] || []).slice().forEach(callback => {
            callback(message);
        });
    }
}



// WEBPACK FOOTER //
// ./Client/v4/Utility/AzurePortalMessenger.ts